import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import styled, { css } from 'styled-components';
import { contentStyles, h1, h2, h3, h4, h5, link, paragraph } from '@helpers/typography';
import { fluid } from '@helpers/fluid';
import { transition } from '@helpers/animate';
import ButtonStyles from '../Global/Buttons/Button/Button.styles';
import fonts from '@helpers/fonts';
const Container = styled.section `
  --gap: 26px;
  display: block;
`;
const Form = styled.form `
  display: block;
  max-width: 900px;
`;
const Fieldset = styled.fieldset `
  border: none;
  padding: 0;
  margin: 0;
  display: block;

  &[aria-current='false'] {
    ${srOnly}
  }

  & + & {
    margin: 24px 0 0;
  }

  margin-right: calc(var(--gap) * -1);
`;
const Legend = styled.legend `
  ${fonts.cabin.bold};
  ${h4};

  display: block;
  margin: 0 0 12px;
  text-transform: uppercase;
`;
const FieldGroup = styled.div `
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: var(--gap);
  margin-bottom: var(--gap);
`;
const FieldRow = styled.div `
  --colWidth: 100%;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: var(--gap);
  height: fit-content;

  flex: 1 0 calc(var(--colWidth) - var(--gap));
  max-width: calc(var(--colWidth) - var(--gap));

  @media ${from(Device.TabletSmall)} {
    &[data-columns='3'],
    &[data-columns='6'] {
      --colWidth: 50%;
    }
  }

  @media ${from(Device.TabletLarge)} {
    &[data-columns='1'],
    &[data-columns='2'],
    &[data-columns='4'],
    &[data-columns='5'] {
      --colWidth: 33.333%;
    }
  }
`;
const Field = styled.div `
  ${h5};
  position: relative;
  display: block;
  margin: 0;
  width: 100%;

  &[hidden] {
    display: none;
  }

  &[data-sr-only='true'] {
    ${srOnly}
  }
  &.AttributerHiddenText {
    ${srOnly}
  }
`;
const Label = styled.label `
  display: block;
  ${fonts.cabin.bold}
  font-size: 18px;
  line-height: 24px;
  margin-bottom: 9px;
`;
const LabelMulti = styled.label `
  ${fonts.cabin.regular}
  margin-bottom: ${fluid(10, 12)};
`;
const ControlLabel = styled.label `
  ${fonts.cabin.regular};
  display: block;
  font-size: 16px;
  line-height: 22px;
  margin: 0 0 12px;
  font-weight: 400;

  svg {
    height: 16px;
    margin-right: 5px;
  }

  > a {
    ${link};
  }

  > input {
    ${srOnly};
  }
`;
const CheckboxLabel = styled.span `
  position: relative;
  padding-left: 52px;
  margin-top: 4px;

  &::after,
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 0;
    display: block;
    height: 30px;
    width: 30px;
  }

  &::after {
    ${transition('opacity', 0.25)};

    content: '✔';
    font-size: 18px;
    height: 30px;
    line-height: 30px;
    opacity: 0;
    text-align: center;
    width: 30px;
    color: var(--accentColorPrimary);
  }

  &::before {
    border: 1px solid var(--fgColor);
    border-radius: calc(var(--borderRadius) * 0.5);
  }

  input:checked + & {
    &::after {
      opacity: 1;
    }
  }

  .invalid &::before {
    border-color: var(--invalidColor);
  }
`;
const RadioLabel = styled.span `
  position: relative;
  padding-left: 32px;

  &::after,
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    display: block;
    border-radius: 50%;
    height: 16px;
    width: 16px;
    transform: translateY(-50%);
  }

  &::after {
    ${transition('opacity', 0.25)};

    content: '✔';
    font-size: 12px;
    height: 18px;
    line-height: 16px;
    opacity: 0;
    text-align: center;
    width: 18px;
  }

  &::before {
    border: 1px solid var(--fgColor);
  }

  input:checked + & {
    &::after {
      opacity: 1;
    }
  }

  .invalid &::before {
    border-color: var(--invalidColor);
  }
`;
const sharedInputStyles = css `
  font-weight: 400;
  ${transition('border-color, background-color')}

  display: block;
  background: var(--bgColor);
  border: 1px solid var(--accentColorSecondary);
  background-color: hsla(var(--fgColorString) / 0.025);
  color: var(--fgColor);
  height: 52px;
  padding: 18px 16px;
  width: 100%;
  border-radius: calc(var(--borderRadius) * 0.5);

  .invalid & {
    border-color: var(--invalidColor);
  }

  &:focus-visible {
    border-color: var(--validColor);
    background-color: hsla(var(--fgColorString) / 0.05);
    outline: auto;
    outline-offset: 5px;
  }
`;
const Input = styled.input `
  ${sharedInputStyles};

  /* &::placeholder {
    color: transparent;
    opacity: 0;
  } */

  &:not(:placeholder-shown) + ${Label}, &:focus + ${Label} {
    font-size: ${fluid(14, 16)};
    line-height: ${fluid(19, 22)};
    transform: translateY(-29px);
    max-height: 22px;
    overflow: hidden;
    background: none;
    background-image: linear-gradient(to bottom, var(--formBgColor) 11px, #fff 15px);

    @media ${from(Device.TabletLarge)} {
      transform: translateY(-33px);
      background-image: linear-gradient(to bottom, var(--formBgColor) 12px, #fff 17px);
    }
  }
`;
const Arrow = styled.div `
  ${transition('border-color, color')};

  display: grid;
  place-items: center;
  height: 52px;
  width: 52px;
  border-left: 1px solid var(--accentColorSecondary);
  position: absolute;
  top: 0;
  right: 0;
  pointer-events: none;

  > svg {
    ${transition('transform')};

    display: block;
    height: 6px;
    width: 12px;
    color: currentColor;
  }

  .invalid & {
    border-color: var(--invalidColor);
    color: var(--invalidColor);
  }
`;
const Select = styled.select `
  ${sharedInputStyles};
  appearance: none;
  padding-right: 52px;
  padding-top: 0;
  padding-bottom: 0;

  &[multiple] {
    height: initial;
    background: none;
  }

  option {
    background-color: var(--bgColor);
    color: var(--fgColor);
  }

  &:focus ~ ${Arrow} > svg {
    transform: rotate(180deg);
  }
`;
const SelectHolder = styled.div `
  position: relative;
`;
const Textarea = styled.textarea `
  ${sharedInputStyles};
  ${fonts.cabin.regular};
  height: auto;
  min-height: 163px;
`;
const HelpText = styled.span `
  ${fonts.cabin.regular}
  display: block;
  font-size: 16px;
  line-height: 22px;
  margin: 4px 0 0;
`;
const Error = styled.span `
  ${fonts.cabin.regular}
  font-weight: 400;

  display: block;
  font-size: 12px;
  line-height: 22px;
  margin: -4px 0 0;
  color: var(--invalidColor);
`;
const Submit = styled.div `
  display: flex;
  align-items: center;
  flex-direction: row;
  margin: 6px 0 0;

  > ${ButtonStyles.Container} {
    margin-top: 0 !important;
  }

  @media ${until(Device.Tablet)} {
    width: 100%;

    ${ButtonStyles.Container} {
      width: 100%;
      justify-content: center;
    }
  }
`;
const SubmitIndicator = styled.div `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 16px;
`;
const ConfirmationMessage = styled.p `
  ${contentStyles};
`;
const ErrorList = styled.ul `
  display: block;
  margin: 0 0 ${fluid(32, 48)};
  color: var(--invalidColor);
`;
const ErrorListItem = styled.li `
  ${paragraph};

  margin: 0 0 ${fluid(8, 12)};
`;
const FieldTitle = styled.h2 `
  display: block;
  width: 100%;
  font-size: 120%;

  &[data-heading='h1'] {
    ${h1};
  }

  &[data-heading='h2'] {
    ${h2};
  }

  &[data-heading='h3'] {
    ${h3};
  }

  &[data-heading='h4'] {
    ${h4};
  }

  &[data-heading='h5'] {
    ${h5};
  }
`;
const RichText = styled.div `
  ${contentStyles}
`;
const FieldDescription = styled.p `
  font-weight: 400;

  display: block;
  width: 100%;
  margin: 0 0 12px;
`;
// File Upload Styles
const FileUploadLabelContainer = styled.div `
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  overflow: hidden;

  > label {
    text-decoration: none;
    margin: 0;
    white-space: nowrap;

    &:hover {
      cursor: pointer;
    }
  }
`;
const FileNameContent = styled.span `
  ${fonts.cabin.regular};
  display: block;
  font-size: 16px;
  line-height: 26px;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`;
const HiddenLabel = styled.span `
  ${srOnly}
`;
const HiddenInput = styled.input `
  ${srOnly}
`;
const ButtonWrapper = styled.div `
  display: flex;
  align-items: center;
`;
const Previous = styled.div ``;
const Next = styled.div ``;
export default {
    Arrow,
    ButtonWrapper,
    CheckboxLabel,
    ConfirmationMessage,
    Container,
    ControlLabel,
    Error,
    ErrorList,
    ErrorListItem,
    Field,
    FieldDescription,
    FieldGroup,
    FieldRow,
    FieldTitle,
    Fieldset,
    FileNameContent,
    FileUploadLabelContainer,
    Form,
    HelpText,
    HiddenLabel,
    HiddenInput,
    Input,
    Next,
    Previous,
    Label,
    LabelMulti,
    Legend,
    RadioLabel,
    RichText,
    Select,
    SelectHolder,
    Submit,
    SubmitIndicator,
    Textarea,
};
